// OIDC Client TS implementation for Management
// Migrated from vue-oidc-client to oidc-client-ts for Authorization Code + PKCE (APD-13289)
// https://github.com/authts/oidc-client-ts

/* eslint-disable @typescript-eslint/naming-convention */

import { UserManager, WebStorageStateStore, type User, type UserProfile } from "oidc-client-ts";
import { reactive } from "vue";
import { DataRequest } from "@/shared/support/Data";
import { SignalR } from "./support/SignalR";
import Globals from "./support/Globals";
import type { Router, RouteLocationNormalized } from "vue-router";

const loc = globalThis.location;
const appUrl = `${loc.protocol}//${loc.host}${import.meta.env.BASE_URL}`;
const silentRedirect = `${appUrl}${(appUrl.endsWith("/") ? "" : "/")}auth/signinsilent/main.html`;

// OIDC wrapper that provides compatibility with the old vue-oidc-client interface
class OidcAuthWrapper {
    private userManager: UserManager | null = null;
    private currentUser: User | null = null;
    private silentRenewErrorCount = 0;
    private readonly maxSilentRenewErrors = 3;

    // Helper to check if error is invalid_grant and redirect to login
    private handleSilentRenewError(err: Error, source: string): boolean {
        const isInvalidGrant = err.message?.includes("invalid_grant") ||
            (err as { error?: string }).error === "invalid_grant";

        if (isInvalidGrant) {
            console.warn(`identity: refresh token invalid (${source}), redirecting to login`, err);
            this.silentRenewErrorCount = 0;
            sessionStorage.setItem("oidc_return_url", globalThis.location.href);
            this.userManager!.signinRedirect();
            return true; // handled
        }
        return false; // not handled
    }

    get isAuthenticated(): boolean {
        return this.currentUser !== null && !this.currentUser.expired;
    }

    get accessToken(): string | null {
        return this.currentUser?.access_token ?? null;
    }

    get userProfile(): UserProfile | null {
        return this.currentUser?.profile ?? null;
    }

    initialize(): void {
        this.userManager = new UserManager({
            authority: Globals.IdentityUrl(""),
            client_id: Globals.GetIdentityClientId(),
            redirect_uri: `${appUrl}auth/callback`,
            post_logout_redirect_uri: appUrl,
            response_type: "code",
            scope: "openid profile offline_access roles phone email auggiemanagementapi",
            silent_redirect_uri: silentRedirect,
            automaticSilentRenew: true,
            loadUserInfo: true,
            userStore: new WebStorageStateStore({ store: globalThis.localStorage }),
        });

        // Wire up events
        this.userManager.events.addUserLoaded((user: User): void => {
            console.debug("identity: user loaded", user);
            this.currentUser = user;
            this.silentRenewErrorCount = 0;

            SignalR.startTechSession();

            // Post to API that a user logged on
            const dr: DataRequest = new DataRequest();
            dr.autoToastOnFailure = false;
            dr.$post("/Service/Users/Load");
        });

        this.userManager.events.addUserUnloaded((): void => {
            console.debug("identity: user unloaded");
            this.currentUser = null;
        });

        this.userManager.events.addUserSignedOut((): void => {
            console.debug("identity: user signed out");
            this.currentUser = null;
        });

        this.userManager.events.addAccessTokenExpiring((): void => {
            console.debug("identity: access token expiring");
        });

        this.userManager.events.addAccessTokenExpired((): void => {
            // Token already expired - automaticSilentRenew only handles "expiring" not "expired"
            // Manually trigger silent renewal (addresses oidc-client-ts issue #1601)
            console.debug("identity: access token expired, attempting silent renewal");
            this.userManager!.signinSilent().catch((err: Error) => {
                if (!this.handleSilentRenewError(err, "expired")) {
                    console.error("identity: silent renewal after expiry failed", err);
                }
            });
        });

        this.userManager.events.addSilentRenewError((err: Error): void => {
            if (this.handleSilentRenewError(err, "silent-renew")) return;

            this.silentRenewErrorCount++;
            console.error(`identity: silent renew error (${this.silentRenewErrorCount}/${this.maxSilentRenewErrors})`, err);

            if (this.silentRenewErrorCount >= this.maxSilentRenewErrors) {
                // Too many failures - redirect to login
                console.warn("identity: max silent renew errors reached, redirecting to login");
                this.silentRenewErrorCount = 0;
                sessionStorage.setItem("oidc_return_url", globalThis.location.href);
                this.userManager!.signinRedirect();
            }
        });

        // Handle sleep/wake scenarios using Page Visibility API (addresses oidc-client-ts issue #251)
        // When the page becomes visible after sleep, check if token needs renewal
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState !== "visible") return;
            if (!this.currentUser) return;

            // Check if token is expired or expiring soon (within 60 seconds)
            const expiresIn = (this.currentUser.expires_at ?? 0) - Math.floor(Date.now() / 1000);
            if (expiresIn < 60) {
                console.debug(`identity: page visible, token ${expiresIn < 0 ? "expired" : "expiring soon"}, attempting renewal`);
                this.userManager?.signinSilent().catch((err: Error) => {
                    if (!this.handleSilentRenewError(err, "visibility")) {
                        console.error("identity: visibility change renewal failed", err);
                    }
                });
            }
        });
    }

    async startup(): Promise<boolean> {
        if (!this.userManager) return false;

        try {
            // Check if we're handling a callback
            if (globalThis.location.pathname === "/auth/callback") {
                try {
                    await this.userManager.signinRedirectCallback();
                    // Redirect to the original URL or home
                    const returnUrl = sessionStorage.getItem("oidc_return_url") || "/";
                    sessionStorage.removeItem("oidc_return_url");
                    globalThis.location.href = returnUrl;
                    return true;
                } catch (error) {
                    // Handle "No matching state found in storage" error
                    // This occurs when user bookmarks/refreshes callback URL or state expired
                    console.warn("identity: callback processing failed, redirecting to home", error);
                    globalThis.location.href = "/";
                    return false;
                }
            }

            // Try to get user from storage
            this.currentUser = await this.userManager.getUser();
            if (this.currentUser && !this.currentUser.expired) {
                console.debug("identity: user restored from storage");
                SignalR.startTechSession();
                return true;
            }
            return false;
        } catch (err) {
            console.error("identity: startup error", err);
            return false;
        }
    }

    async signIn(returnUrl?: string): Promise<void> {
        if (!this.userManager) return;
        sessionStorage.setItem("oidc_return_url", returnUrl || globalThis.location.href);
        await this.userManager.signinRedirect();
    }

    async signOut(): Promise<void> {
        if (!this.userManager) return;
        await this.userManager.signoutRedirect();
    }

    useRouter(router: Router): void {
        // Add navigation guard for authentication
        router.beforeEach(async (to: RouteLocationNormalized) => {
            // Skip auth check for callback route
            if (to.path === "/auth/callback") {
                return true;
            }

            // Check roles if specified
            const allowedRoles = to.meta.allowedRoles as string[] | undefined;
            if (allowedRoles && allowedRoles.length > 0) {
                if (UserRoles.hasAny(allowedRoles)) {
                    return true;
                } else if (this.isAuthenticated) {
                    return { path: "/PageNotFound" };
                }
                // Not authenticated - let them through (vue-oidc-client behavior)
                return true;
            }

            // No auth required
            return true;
        });
    }
}

// Wrap in reactive() so Vue tracks property changes (isAuthenticated, etc.) for template bindings
export const identity = reactive(new OidcAuthWrapper()) as OidcAuthWrapper;

export class CurrentUser {

    private static authenticated = true; // must default to true in case of page reloads without User loading

    /**
     * Returns whether the user is authenticated.
     */
    public static get isAuthenticated(): boolean {
        return CurrentUser.authenticated && identity.isAuthenticated;
    }

    public static get userProfile(): UserProfile | null {
        if (CurrentUser.isAuthenticated)
            return identity.userProfile;
        return null;
    }

    public static get userGuid(): string | null {
        if (CurrentUser.isAuthenticated) {
            return identity.userProfile?.sub ?? null;
        }
        return null;
    }

    /**
     * Defines whether the User was loaded and has been established as logged on with the WebAPI.
     * FOR INTERNAL USE ONLY.
     */
    public static setAuthentication(auth: boolean): void {
        CurrentUser.authenticated = auth && identity.isAuthenticated;
    }

    /**
     * Returns whether the User was loaded and has been established as logged on with the WebAPI.
     * FOR INTERNAL USE ONLY.
     */
    public static verifyAuthentication(): boolean {
        return CurrentUser.authenticated;
    }
}

/**
 * Class to manage/test user roles.
 */
export class UserRoles {
    /**
     * Tests whether the currently authenticated user (if any) has the required user role.
     * @param role The user role to test.
     * @returns true if the user has the required role, false otherwise.
     */
    public static has(role: string): boolean {
        const profile = CurrentUser.userProfile;
        if (!profile) return false;
        const roles = profile["role"] as string[] | null;
        if (!roles) return false;
        return roles.find((v) => v === role) != null;
    }
    /**
     * Tests whether the currently authenticated user (if any) has at least one of the required user roles.
     * @param roles The list of user roles to test.
     * @returns true if the user has at least one of the required role, false otherwise.
     */
    public static hasAny(roles: string[]): boolean {
        for (const role of roles) {
            if (UserRoles.has(role))
                return true;
        }
        return false;
    }
}

export class Access {
    public static getToken(): string | null {
        if (!CurrentUser.isAuthenticated) return null;
        return identity.accessToken;
    }
}
